
// Types
import type { Partners } from '@white-label-types/partners';

// Constants
import { PAYMENT_PROVIDER_COLORS, PAYMENT_PROVIDERS } from '@white-label-configuration/constants';

// Packages
import { defineComponent, PropType } from 'vue';

// Helpers
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentMerchantId } from '@white-label-helper/get-app-payment-merchant-id';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';

export default defineComponent({
  props: {
    removeButton: {
      default: true,
      type: Boolean,
    },

    amount: {
      type: [String, Number] as PropType<string | number>,
      required: true,
    },

    paymentsConfig: {
      type: Object,
      required: true,
    },

    paymentIntentUpdated: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isIntendedSubmit: false,
      interceptorTimer: null as ReturnType<typeof setTimeout> | null,
    };
  },

  computed: {
    stripeConfig(): string {
      const isStripe = getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE;

      return JSON.stringify({
        ...this.paymentsConfig,
        stripeHandlePaymentIntent: isStripe,
      });
    },

    stripeStyles(): string {
      return JSON.stringify({
        colorPrimary: PAYMENT_PROVIDER_COLORS.STRIPE.PRIMARY,
        colorBackground: PAYMENT_PROVIDER_COLORS.STRIPE.BACKGROUND,
        colorText: PAYMENT_PROVIDER_COLORS.STRIPE.TEXT,
        colorDanger: PAYMENT_PROVIDER_COLORS.STRIPE.DANGER,
        fontFamily: 'Ideal Sans, system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '0.5rem',
      });
    },

    currency(): Partners['default_currency'] {
      return getAppVariable('default_currency');
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.attachSubmitInterceptor();
    });
  },

  beforeDestroy() {
    if (this.interceptorTimer) {
      clearTimeout(this.interceptorTimer);
    }
  },

  methods: {
    //for intercepting form submission when pressing Enter on card fields
    attachSubmitInterceptor() {
      const btn: HTMLButtonElement | null = document.querySelector('#submit');

      if (!btn) {
        this.interceptorTimer = setTimeout(this.attachSubmitInterceptor, 200);
        return;
      }

      btn.addEventListener('click', this.blockUnauthorisedSubmit, true);
    },

    blockUnauthorisedSubmit(e: Event) {
      if (!this.isIntendedSubmit) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },

    handlePayload(event: any) {
      this.$emit('payment-completed', {
        payment_method_id: event.detail.nonce,
        merchant_id: getAppPaymentMerchantId(),
      });
    },

    handleError() {
      this.$emit('error');
    },

    handleStripePaymentIntentPayload() {
      this.$emit('stripe-payment-intent-payload', {
        payment_method_id: null,
        merchant_id: getAppPaymentMerchantId(),
      });
    },

    submitForm() {
      // This button is located in the `stripe-ui > stripe-hosted-fields` element
      this.isIntendedSubmit = true;
      const btn: HTMLButtonElement | null = document.querySelector('#submit');
      btn?.click();
      setTimeout(() => {
        this.isIntendedSubmit = false;
      }, 300);
    },
  },
});
